import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';

const SILENCE_MS = 2200;
const SANS_PAROLE_MS = 8000;
const MAX_MS = 90000;
const HALLUCINATIONS = /sous-titr|amara\.org|merci d'avoir regard|abonnez-vous/i;

// Enregistre au micro, s'arrête tout seul après un silence, puis transcrit avec Whisper
export function useVoiceRecorder({ onText, onError }) {
  const [etat, setEtat] = useState('idle');
  const refs = useRef({});
  const onTextRef = useRef(onText);
  const onErrorRef = useRef(onError);
  onTextRef.current = onText;
  onErrorRef.current = onError;

  const nettoyer = () => {
    const r = refs.current;
    if (r.timer) clearInterval(r.timer);
    if (r.stream) r.stream.getTracks().forEach((t) => t.stop());
    if (r.ctx && r.ctx.state !== 'closed') r.ctx.close().catch(() => {});
    refs.current = { recorder: r.recorder };
  };

  const stop = useCallback((annuler = false) => {
    const r = refs.current;
    r.annuler = annuler;
    if (r.recorder && r.recorder.state !== 'inactive') r.recorder.stop();
  }, []);

  const transcrire = async (blob, type) => {
    setEtat('transcribing');
    try {
      const fd = new FormData();
      fd.append('file', blob, type.includes('mp4') ? 'voix.mp4' : 'voix.webm');
      const res = await api.post('/integrations/transcribe/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const texte = (res.data?.text || '').trim();
      if (texte && !HALLUCINATIONS.test(texte)) onTextRef.current?.(texte);
      else onErrorRef.current?.("Je n'ai rien entendu, réessaie.");
    } catch {
      onErrorRef.current?.('Transcription impossible, réessaie.');
    }
    setEtat('idle');
  };

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      onErrorRef.current?.('Micro non supporté sur ce navigateur');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch {
      onErrorRef.current?.('Microphone non autorisé');
      return;
    }
    const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t)) || '';
    const recorder = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const morceaux = [];
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const debut = Date.now();
    let derniereVoix = 0;
    let aParle = false;
    let bruitFond = 0;
    let mesures = 0;

    refs.current = { stream, ctx, recorder, annuler: false };
    recorder.ondataavailable = (e) => { if (e.data.size) morceaux.push(e.data); };
    recorder.onstop = () => {
      const annuler = refs.current.annuler;
      nettoyer();
      if (annuler || !aParle || !morceaux.length) { setEtat('idle'); return; }
      transcrire(new Blob(morceaux, { type: recorder.mimeType || 'audio/webm' }), recorder.mimeType || 'audio/webm');
    };

    refs.current.timer = setInterval(() => {
      analyser.getFloatTimeDomainData(buf);
      const rms = Math.sqrt(buf.reduce((s, v) => s + v * v, 0) / buf.length);
      const maintenant = Date.now();
      if (mesures < 4) { bruitFond = Math.max(bruitFond, rms); mesures += 1; return; }
      const seuil = Math.max(0.012, bruitFond * 2.5);
      if (rms > seuil) { aParle = true; derniereVoix = maintenant; }
      if (aParle && maintenant - derniereVoix > SILENCE_MS) stop();
      else if (!aParle && maintenant - debut > SANS_PAROLE_MS) stop(true);
      else if (maintenant - debut > MAX_MS) stop();
    }, 100);

    recorder.start(250);
    setEtat('listening');
  }, [stop]);

  useEffect(() => () => { stop(true); }, [stop]);

  return { etat, start, stop };
}
